import { useTranslation } from 'common/src/hooks/useTranslation';
import {
  getShipmentLogbookSummaries,
  type ShipmentLogbookSummary,
} from 'common/src/api/shipment-logbook';
import { ShipmentLogbookRowSummary } from '../../../components/shipments/ShipmentLogbook';
import useReceiverStore from '../../../store/useReceiverStore';
import type { ShipmentBox } from 'common/src/utils/types';
import { canRegisterShipmentPrices } from '../../../utils/shipment-price-access';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Button,
  Card,
  CardBody,
  Chip,
  Tooltip,
  Tabs,
  Tab,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';

import useUserStore from '../../../../../common/src/stores/useUserStore';
import { cn } from '../../../../../common/src/utils/cn';
import {
  Alert,
  EmptyState,
  KpiCard,
  LoadingState,
  MapFormatsButton,
  PageHeader,
  PageLayout,
} from '../../../components/ui';
import useFilterStore from '../../../../../common/src/stores/useFiltersStore';
import useDataStore from '../../../../../common/src/stores/useDataStore';
import {
  getArrivalData,
  getTrackingRoutes,
  refreshArrivalTracking,
  type ArrivalData,
  type TrackingRoute,
} from 'common/src/api/shipment-arrivals';
import ArrivalsMapPanel from './ArrivalsMapPanel';
import { buildArrivalMap, mapFilterIds, type MapFilter } from './map-model';
import ShipmentDetailsModal from '../../../components/shipments/ShipmentDetailsModal';
import FormatMappingModal from '../../../components/frutic-box-mapping/FormatMappingModal';
import { useShipmentDetails } from '../../../hooks/useShipmentDetails';
import { exporterUsesFrutic } from 'common/src/utils/frutic-functions';
import { useScopedRequest } from './use-scoped-request';
import { normalizeTransport } from '../../../utils/client-reports/build-shipments-section';
import {
  arrivalWindow,
  overdueArrivals,
  buildArrivals,
  containerCount,
  fruitAge,
  ageBucket,
  canRefreshArrival,
  unitLabel,
  type Arrival,
  type ArrivalGroupKey,
  type ArrivalSubGroupKey,
  type TimelineMetric,
} from './model';

const GROUP_LABELS: Record<ArrivalGroupKey, string> = {
  pod: 'POD',
  cliente: 'Cliente',
  fecha: 'Fecha ETA',
};

const MONTHS = [
  'ene',
  'feb',
  'mar',
  'abr',
  'may',
  'jun',
  'jul',
  'ago',
  'sept',
  'oct',
  'nov',
  'dic',
];
const WEEKDAYS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
const SEGMENT_COLORS = [
  'hsl(var(--nextui-primary))',
  'hsl(var(--nextui-secondary))',
  'hsl(var(--nextui-success))',
  'hsl(var(--nextui-warning))',
  'hsl(var(--nextui-danger))',
  'hsl(var(--nextui-default-500))',
];

const AGE_COLORS: Record<string, string> = {
  '≤ 25 días': 'hsl(var(--nextui-success))',
  '26–35 días': 'hsl(var(--nextui-secondary))',
  '36–45 días': 'hsl(var(--nextui-warning))',
  '> 45 días': 'hsl(var(--nextui-danger))',
  'Sin fecha de embalaje': 'hsl(var(--nextui-default-400))',
};

const startOfToday = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

const dateForOffset = (offsetDays: number) => {
  const date = startOfToday();
  date.setDate(date.getDate() + offsetDays);
  return date;
};

const formatDate = (date: Date) =>
  `${String(date.getDate()).padStart(2, '0')} ${MONTHS[date.getMonth()]}`;

const formatKg = (kg: number) => `${Math.round(kg).toLocaleString('es-CL')} kg`;

const relativeDate = (offsetDays: number) => {
  if (offsetDays === 0) return 'Hoy';
  if (offsetDays === 1) return 'Mañana';
  if (offsetDays < 0) return `Vencida hace ${Math.abs(offsetDays)} días`;
  return `en ${offsetDays} días`;
};

interface CompositionSegment {
  label: string;
  kg: number;
  percentage: number;
  color: string;
}

interface CompositionDimension {
  label: string;
  segments: CompositionSegment[];
}

const computeComposition = (arrivals: Arrival[]): CompositionDimension[] => {
  const totalKg =
    arrivals.reduce((total, arrival) => total + arrival.kg, 0) || 1;
  const dimensions: Array<
    [string, (arrival: Arrival, lineIndex: number) => string]
  > = [
    ['Variedad', (arrival, index) => arrival.mix[index].variety],
    ['Calibre', (arrival, index) => arrival.mix[index].size],
    ['Color', (arrival, index) => arrival.mix[index].color],
    [
      'Formato · Peso caja',
      (arrival, index) =>
        `${arrival.mix[index].format} · ${arrival.mix[index].boxWeight || 'Sin peso'} kg`,
    ],
    ['Etiqueta', (arrival, index) => arrival.mix[index].label],
    ['Productores', (arrival, index) => arrival.mix[index].producer],
    [
      'Días desde embalaje (al arribo)',
      (arrival, index) => ageBucket(arrival.mix[index].ageDays),
    ],
  ];

  return dimensions.map(([label, getKey]) => {
    const totals = new Map<string, number>();
    arrivals.forEach((arrival) => {
      arrival.mix.forEach((line, index) => {
        const key = getKey(arrival, index);
        totals.set(key, (totals.get(key) ?? 0) + line.kg);
      });
    });

    return {
      label,
      segments: [...totals.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([segmentLabel, kg], index) => ({
          label: segmentLabel,
          kg,
          percentage: (kg / totalKg) * 100,
          color:
            label === 'Días desde embalaje (al arribo)'
              ? AGE_COLORS[segmentLabel]
              : SEGMENT_COLORS[index % SEGMENT_COLORS.length],
        })),
    };
  });
};

const AgeBar = ({ arrivals }: { arrivals: Arrival[] }) => {
  const segments = useMemo(() => {
    const buckets = Object.entries(AGE_COLORS).map(([label, color]) => ({
      label,
      color,
    }));
    const totalKg =
      arrivals.reduce((total, arrival) => total + arrival.kg, 0) || 1;
    const totals = new Map<string, number>();

    arrivals.forEach((arrival) => {
      arrival.mix.forEach((line) => {
        const bucket = ageBucket(line.ageDays);
        totals.set(bucket, (totals.get(bucket) ?? 0) + line.kg);
      });
    });

    return buckets
      .filter((bucket) => totals.has(bucket.label))
      .map((bucket) => ({
        ...bucket,
        kg: totals.get(bucket.label) ?? 0,
        percentage: ((totals.get(bucket.label) ?? 0) / totalKg) * 100,
      }));
  }, [arrivals]);

  return (
    <div className='flex h-1.5 w-20 shrink-0 overflow-hidden rounded-full bg-default-200'>
      {segments.map((segment) => (
        <Tooltip
          key={segment.label}
          content={`${segment.label} desde embalaje · ${formatKg(segment.kg)} (${Math.round(segment.percentage)}%)`}
        >
          <span
            className='h-full'
            style={{
              width: `${segment.percentage}%`,
              backgroundColor: segment.color,
            }}
          />
        </Tooltip>
      ))}
    </div>
  );
};

/** A box whose format code has no CherryMarket format yet. */
const isUnmappedFormat = (format: string) =>
  ['NO ENCONTRADO', 'NA', 'N/A', 'SIN DATO'].includes(
    format.trim().toUpperCase()
  );
const unmappedFormatKg = (arrivals: Arrival[]) =>
  arrivals.reduce(
    (total, arrival) =>
      total +
      arrival.mix.reduce(
        (sum, line) => sum + (isUnmappedFormat(line.format) ? line.kg : 0),
        0
      ),
    0
  );
/** Opens the format mapping modal from any composition panel. */
const MapFormatsContext = createContext<(() => void) | null>(null);

const CompositionPanel = ({
  arrivals,
  title,
}: {
  arrivals: Arrival[];
  title: string;
}) => {
  const dimensions = useMemo(() => computeComposition(arrivals), [arrivals]);
  const totalKg = arrivals.reduce((total, arrival) => total + arrival.kg, 0);
  const openFormatMapping = useContext(MapFormatsContext);
  const hasUnmappedFormats = unmappedFormatKg(arrivals) > 0;

  return (
    <div className='border-t border-default-200 bg-default-50/60 px-4 py-5 sm:px-6'>
      <div className='mb-4 flex flex-wrap items-center justify-between gap-2'>
        <span className='text-xs font-semibold uppercase tracking-wide text-default-500'>
          {title}
        </span>
        <span className='text-xs text-default-400'>
          {unitLabel(arrivals)} · {formatKg(totalKg)}
        </span>
      </div>
      <div className='grid gap-5 sm:grid-cols-2 xl:grid-cols-4'>
        {dimensions.map((dimension) => (
          <div key={dimension.label} className='min-w-0'>
            <div className='flex min-h-6 items-center justify-between gap-2'>
              <p className='text-xs font-semibold text-default-500'>
                {dimension.label}
              </p>
              {dimension.label === 'Formato · Peso caja' &&
                hasUnmappedFormats &&
                openFormatMapping && (
                  <MapFormatsButton onPress={openFormatMapping} />
                )}
            </div>
            <div className='mt-2 flex h-2 overflow-hidden rounded-full bg-default-200'>
              {dimension.segments.map((segment) => (
                <Tooltip
                  key={segment.label}
                  content={`${segment.label}: ${formatKg(segment.kg)}`}
                >
                  <span
                    className='h-full'
                    style={{
                      width: `${segment.percentage}%`,
                      backgroundColor: segment.color,
                    }}
                  />
                </Tooltip>
              ))}
            </div>
            <div className='mt-2 space-y-1.5'>
              {dimension.segments.map((segment) => (
                <div
                  key={segment.label}
                  className='flex min-w-0 items-center gap-2 text-xs'
                >
                  <span
                    className='h-2 w-2 shrink-0 rounded-sm'
                    style={{ backgroundColor: segment.color }}
                  />
                  <span className='truncate text-foreground'>
                    {segment.label}
                  </span>
                  <span className='ml-auto shrink-0 text-default-400'>
                    {formatKg(segment.kg)}
                  </span>
                  <span className='w-9 shrink-0 text-right font-semibold text-default-500'>
                    {Math.round(segment.percentage)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const SegmentedControl = <T extends string>({
  value,
  options,
  onChange,
  disabled,
}: {
  value: T;
  options: Array<{ key: T; label: string }>;
  onChange: (value: T) => void;
  disabled?: (value: T) => boolean;
}) => (
  <Tabs
    aria-label={options.map((option) => option.label).join(' / ')}
    size='sm'
    selectedKey={value}
    disabledKeys={options
      .filter((option) => disabled?.(option.key))
      .map((option) => option.key)}
    onSelectionChange={(key) => onChange(key as T)}
  >
    {options.map((option) => (
      <Tab key={option.key} title={option.label} />
    ))}
  </Tabs>
);

interface ArrivalRowProps {
  arrival: Arrival;
  expanded: boolean;
  showPod: boolean;
  onToggleExpanded: () => void;
  onTracking: () => void;
  onLogbook?: () => void;
  management?: ShipmentLogbookSummary;
}

const ArrivalRow = ({
  arrival,
  expanded,
  showPod,
  onToggleExpanded,
  onTracking,
  onLogbook,
  management,
}: ArrivalRowProps) => {
  const { t } = useTranslation('shipments');
  const eta =
    arrival.offsetDays === null ? null : dateForOffset(arrival.offsetDays);
  const ageInfo = fruitAge([arrival]);
  const age = ageInfo.days === null ? null : Math.round(ageInfo.days);
  const urgent = arrival.urgent;
  const soon =
    arrival.offsetDays !== null &&
    arrival.offsetDays >= 0 &&
    arrival.offsetDays <= 5;
  const milestoneColor = arrival.arrived
    ? 'success'
    : ['Nave zarpó', 'Vuelo despegó'].includes(arrival.milestone)
      ? 'primary'
      : 'default';

  return (
    <div className='border-t border-default-200 transition-colors hover:bg-default-50/70'>
      <div className='grid gap-4 px-4 py-4 lg:grid-cols-[1.2fr_1.05fr_1.4fr_.85fr_1.25fr_auto] lg:items-center lg:px-5'>
        <div className='min-w-0'>
          <p className='truncate font-mono text-sm font-semibold'>
            {arrival.container}
          </p>
          <p className='mt-1 truncate font-mono text-[11px] text-default-400'>
            {arrival.reference}
          </p>
        </div>
        <div className='min-w-0'>
          <p className='truncate text-sm font-semibold'>{arrival.client}</p>
          {management && <ShipmentLogbookRowSummary summary={management} />}
          {showPod && (
            <p className='mt-1 truncate text-xs text-default-400'>
              {arrival.flag} {arrival.portName}
            </p>
          )}
        </div>
        <div className='min-w-0'>
          <Chip size='sm' variant='flat' color={milestoneColor}>
            {arrival.milestone}
          </Chip>
          <p className='mt-1 truncate text-xs text-default-400'>
            {arrival.mode === 'air'
              ? `Aéreo · ${arrival.vessel}`
              : `${arrival.vessel} · Viaje ${arrival.voyage}`}
          </p>
        </div>
        <div>
          <p className='text-sm font-medium'>{formatKg(arrival.kg)}</p>
          <p className='mt-1 text-xs text-default-400'>
            {arrival.quantity.toLocaleString('es-CL')} bultos
          </p>
          <div className='mt-2'>
            <AgeBar arrivals={[arrival]} />
          </div>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <Tooltip
            content={
              arrival.etaSource === 'actual'
                ? 'Arribo real confirmado por tracking'
                : arrival.etaSource === 'tracking'
                  ? 'ETA del último tracking'
                  : 'ETA planificada del despacho'
            }
          >
            <span className='text-sm font-bold'>
              {eta ? formatDate(eta) : 'Sin ETA'} ·{' '}
              {arrival.etaSource === 'actual'
                ? 'ATA'
                : arrival.etaSource === 'tracking'
                  ? 'Tracking'
                  : 'Plan'}
            </span>
          </Tooltip>
          <Chip
            size='sm'
            variant='flat'
            color={urgent ? 'danger' : soon ? 'warning' : 'default'}
          >
            {arrival.arrived
              ? 'Arribo confirmado'
              : arrival.offsetDays === null
                ? 'Sin fecha'
                : relativeDate(arrival.offsetDays)}
          </Chip>
          <Tooltip
            content={`Edad ponderada desde embalaje. ${formatKg(ageInfo.missingKg)} sin fecha de embalaje válida.`}
          >
            <Chip
              size='sm'
              variant='flat'
              color={
                age === null
                  ? 'default'
                  : age > 45
                    ? 'danger'
                    : age > 35
                      ? 'warning'
                      : 'success'
              }
            >
              {age === null ? 'Edad sin dato' : `Fruta ${age}d`}
            </Chip>
          </Tooltip>
          {arrival.delayDays !== null && arrival.delayDays > 0 && (
            <Tooltip
              content={`Respecto de la ETA planificada: ${arrival.plannedEta?.slice(0, 10)}`}
            >
              <Chip size='sm' variant='bordered' color='danger'>
                ETA +{arrival.delayDays}d
              </Chip>
            </Tooltip>
          )}
        </div>
        <div className='flex justify-end gap-1'>
          {onLogbook && (
            <Tooltip content={t('Abrir bitácora')}>
              <Button
                isIconOnly
                size='sm'
                variant='light'
                aria-label={t('Abrir bitácora')}
                onPress={onLogbook}
              >
                <Icon icon='tabler:notes' width={18} />
              </Button>
            </Tooltip>
          )}

          <Tooltip content='Ver composición'>
            <Button
              isIconOnly
              size='sm'
              variant='light'
              color={expanded ? 'danger' : 'default'}
              aria-label='Ver composición'
              onPress={onToggleExpanded}
            >
              <Icon icon='tabler:chart-bar' width={18} />
            </Button>
          </Tooltip>
          <Tooltip content='Ver seguimiento'>
            <Button
              isIconOnly
              size='sm'
              variant='light'
              aria-label='Ver seguimiento'
              onPress={onTracking}
            >
              <Icon icon='tabler:eye' width={18} />
            </Button>
          </Tooltip>
        </div>
      </div>

      {expanded && (
        <div className='px-4 pb-4 lg:px-5'>
          <div className='overflow-hidden rounded-medium border border-default-200'>
            <CompositionPanel
              arrivals={[arrival]}
              title='Composición del contenedor'
            />
          </div>
        </div>
      )}
    </div>
  );
};

interface ArrivalGroup {
  key: string;
  label: string;
  tag?: string;
  flag?: string;
  minOffset: number;
  arrivals: Arrival[];
}

const groupArrivals = (
  arrivals: Arrival[],
  groupBy: ArrivalGroupKey
): ArrivalGroup[] => {
  const groups = new Map<string, ArrivalGroup>();

  arrivals.forEach((arrival) => {
    const eta = dateForOffset(arrival.offsetDays ?? 0);
    const meta =
      groupBy === 'pod'
        ? {
            key: arrival.pod,
            label: arrival.portName,
            tag: arrival.pod,
            flag: arrival.flag,
          }
        : groupBy === 'cliente'
          ? { key: arrival.clientKey, label: arrival.client }
          : {
              key: `day-${arrival.offsetDays}`,
              label: `${WEEKDAYS[eta.getDay()]} ${eta ? formatDate(eta) : 'Sin ETA'}`,
              tag:
                arrival.offsetDays === null
                  ? 'Sin ETA'
                  : relativeDate(arrival.offsetDays),
            };

    const existing = groups.get(meta.key);
    if (existing) {
      existing.arrivals.push(arrival);
      existing.minOffset = Math.min(
        existing.minOffset,
        arrival.offsetDays ?? Infinity
      );
    } else {
      groups.set(meta.key, {
        ...meta,
        minOffset: arrival.offsetDays ?? Infinity,
        arrivals: [arrival],
      });
    }
  });

  return [...groups.values()].sort(
    (a, b) => a.minOffset - b.minOffset || a.label.localeCompare(b.label)
  );
};

const summaryText = (arrivals: Arrival[]) =>
  `${unitLabel(arrivals)} · ${formatKg(arrivals.reduce((total, arrival) => total + arrival.kg, 0))}`;

interface GroupCardProps {
  group: ArrivalGroup;
  groupBy: ArrivalGroupKey;
  subGroupBy: ArrivalSubGroupKey;
  expandedRows: Record<string, boolean>;
  openCompositions: Record<string, boolean>;
  collapsedGroups: Record<string, boolean>;
  onToggleRow: (id: string) => void;
  onTracking: (arrival: Arrival) => void;
  onLogbook?: (arrival: Arrival) => void;
  managementSummaries?: Record<string, ShipmentLogbookSummary>;
  onToggleComposition: (key: string) => void;
  onToggleCollapsed: (key: string) => void;
}

const GroupCard = ({
  group,
  groupBy,
  subGroupBy,
  expandedRows,
  openCompositions,
  collapsedGroups,
  onToggleRow,
  onTracking,
  onLogbook,
  managementSummaries,
  onToggleComposition,
  onToggleCollapsed,
}: GroupCardProps) => {
  const groupKey = `group:${groupBy}:${group.key}`;
  const isCollapsed = !!collapsedGroups[groupKey];
  const compositionOpen = !!openCompositions[groupKey];
  const sections =
    subGroupBy === 'none' || subGroupBy === groupBy
      ? null
      : groupArrivals(group.arrivals, subGroupBy);

  const renderRows = (arrivals: Arrival[]) =>
    arrivals
      .slice()
      .sort((a, b) => (a.offsetDays ?? Infinity) - (b.offsetDays ?? Infinity))
      .map((arrival) => (
        <ArrivalRow
          key={arrival.id}
          arrival={arrival}
          expanded={!!expandedRows[arrival.id]}
          showPod={groupBy !== 'pod' && subGroupBy !== 'pod'}
          onToggleExpanded={() => onToggleRow(arrival.id)}
          onTracking={() => onTracking(arrival)}
          onLogbook={onLogbook ? () => onLogbook(arrival) : undefined}
          management={managementSummaries?.[arrival.shipmentId]}
        />
      ));

  return (
    <Card shadow='sm' className='mt-5'>
      <CardBody className='p-0'>
        <div className='flex items-center gap-3 bg-default-50 px-4 py-3 sm:px-5'>
          <Button
            isIconOnly
            size='sm'
            variant='light'
            aria-label={isCollapsed ? 'Expandir grupo' : 'Contraer grupo'}
            onPress={() => onToggleCollapsed(groupKey)}
          >
            <Icon
              icon='tabler:chevron-right'
              width={18}
              className={cn(
                'transition-transform',
                !isCollapsed && 'rotate-90'
              )}
            />
          </Button>
          {group.flag && <span className='text-xl'>{group.flag}</span>}
          <span
            className='min-w-0 line-clamp-2 text-sm font-bold sm:line-clamp-1 sm:text-base'
            title={group.label}
          >
            {group.label}
          </span>
          {group.tag && (
            <Chip size='sm' variant='flat'>
              {group.tag}
            </Chip>
          )}
          <span className='ml-auto hidden text-xs text-default-500 sm:block'>
            {summaryText(group.arrivals)}
          </span>
          <AgeBar arrivals={group.arrivals} />
          <Tooltip content='Ver composición'>
            <Button
              isIconOnly
              size='sm'
              variant='light'
              color={compositionOpen ? 'danger' : 'default'}
              aria-label='Ver composición del grupo'
              onPress={() => onToggleComposition(groupKey)}
            >
              <Icon icon='tabler:chart-bar' width={18} />
            </Button>
          </Tooltip>
        </div>

        {compositionOpen && (
          <CompositionPanel
            arrivals={group.arrivals}
            title='Composición del grupo'
          />
        )}

        {!isCollapsed &&
          (sections
            ? sections.map((section) => {
                const sectionKey = `${groupKey}:section:${subGroupBy}:${section.key}`;
                const sectionCompositionOpen = !!openCompositions[sectionKey];
                const sectionCollapsed = !!collapsedGroups[sectionKey];
                return (
                  <div key={section.key}>
                    <div className='flex items-center gap-2 border-t border-default-200 bg-default-100/60 px-4 py-2 sm:px-5'>
                      <Button
                        isIconOnly
                        size='sm'
                        variant='light'
                        aria-label={
                          sectionCollapsed
                            ? 'Expandir sección'
                            : 'Contraer sección'
                        }
                        onPress={() => onToggleCollapsed(sectionKey)}
                      >
                        <Icon
                          icon='tabler:chevron-right'
                          width={15}
                          className={cn(
                            'transition-transform',
                            !sectionCollapsed && 'rotate-90'
                          )}
                        />
                      </Button>
                      {section.flag && <span>{section.flag}</span>}
                      <span className='truncate text-xs font-bold uppercase tracking-wide'>
                        {section.label}
                      </span>
                      <span className='ml-auto hidden text-xs text-default-400 sm:block'>
                        {summaryText(section.arrivals)}
                      </span>
                      <AgeBar arrivals={section.arrivals} />
                      <Button
                        isIconOnly
                        size='sm'
                        variant='light'
                        color={sectionCompositionOpen ? 'danger' : 'default'}
                        aria-label='Ver composición de la sección'
                        onPress={() => onToggleComposition(sectionKey)}
                      >
                        <Icon icon='tabler:chart-bar' width={16} />
                      </Button>
                    </div>
                    {sectionCompositionOpen && (
                      <CompositionPanel
                        arrivals={section.arrivals}
                        title={`Composición — ${section.label}`}
                      />
                    )}
                    {!sectionCollapsed && renderRows(section.arrivals)}
                  </div>
                );
              })
            : renderRows(group.arrivals))}
      </CardBody>
    </Card>
  );
};

const ArrivalManagerPage = () => {
  const { t } = useTranslation('shipments');
  const { userProfile, session } = useUserStore();
  const { selectedSeason } = useFilterStore();
  const { selectedSpecies } = useDataStore();
  const exporterId = userProfile?.exporter?.id ?? userProfile?.exporter_id;
  const usesFrutic = exporterUsesFrutic(userProfile?.exporter, selectedSeason);
  const { exporterReceivers, fetchExporterReceivers } = useReceiverStore();
  useEffect(() => {
    if (exporterId)
      void fetchExporterReceivers(exporterId).catch(() => undefined);
  }, [exporterId, fetchExporterReceivers]);
  const getReceiverLabel = useCallback(
    (box: Pick<ShipmentBox, 'exporter_receiver_id' | 'receiver'>) =>
      exporterReceivers.find(
        (receiver) =>
          receiver.id === box.exporter_receiver_id &&
          receiver.exporter_id === exporterId
      )?.name ||
      box.receiver ||
      undefined,
    [exporterId, exporterReceivers]
  );
  const scope =
    session?.user.id && exporterId && selectedSeason && selectedSpecies?.id
      ? JSON.stringify([
          session.user.id,
          userProfile?.id,
          exporterId,
          selectedSeason,
          selectedSpecies.id,
          usesFrutic,
        ])
      : null;
  const load = useCallback(
    async (
      signal?: AbortSignal
    ): Promise<{
      data: ArrivalData;
      management?: Record<string, ShipmentLogbookSummary>;
      managementError?: boolean;
      refresh?: Awaited<ReturnType<typeof refreshArrivalTracking>>;
    }> => {
      if (!exporterId || !selectedSeason || !selectedSpecies?.id)
        throw new Error('Selecciona temporada y especie');
      const data = await getArrivalData(
        exporterId,
        selectedSeason,
        selectedSpecies.id,
        signal
      );
      let management: Record<string, ShipmentLogbookSummary> | undefined,
        managementError = false;
      signal?.throwIfAborted();
      try {
        management = await getShipmentLogbookSummaries(
          exporterId,
          selectedSeason,
          selectedSpecies.id,
          data.cargo.map((c) => Number(c.shipment_id))
        );
      } catch {
        signal?.throwIfAborted();
        managementError = true;
      }
      return { data, management, managementError };
    },
    [exporterId, selectedSeason, selectedSpecies?.id]
  );
  const request = useScopedRequest(scope, load);
  const { reload: reloadArrivalData } = request;
  const reloadArrivals = useCallback(() => {
    void reloadArrivalData();
  }, [reloadArrivalData]);
  const details = useShipmentDetails({
    scope,
    exporterId,
    season: selectedSeason,
    speciesId: selectedSpecies?.id,
    usesFrutic,
    onHeaderSaved: reloadArrivals,
    onTrackingUpdated: reloadArrivals,
    onFormatMapped: reloadArrivals,
  });
  const canRegisterPrices = canRegisterShipmentPrices(userProfile);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60000);
    return () => window.clearInterval(timer);
  }, []);
  const arrivals = useMemo(
    () =>
      request.data
        ? buildArrivals(
            {
              ...request.data.data,
              cargo: request.data.data.cargo.map((box) => ({
                ...box,
                receiver: getReceiverLabel(box) || box.receiver,
              })),
            },
            now
          )
        : [],
    [request.data, now, getReceiverLabel]
  );
  const [horizon, setHorizon] = useState(7);
  const [groupBy, setGroupBy] = useState<ArrivalGroupKey>('pod');
  const [subGroupBy, setSubGroupBy] = useState<ArrivalSubGroupKey>('none');
  const [timelineMetric, setTimelineMetric] =
    useState<TimelineMetric>('containers');
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [openCompositions, setOpenCompositions] = useState<
    Record<string, boolean>
  >({ total: true });
  const [collapsedGroups, setCollapsedGroups] = useState<
    Record<string, boolean>
  >({});
  const [showOverdue, setShowOverdue] = useState(false);
  const [showMap, setShowMap] = useState(true);
  const [mapFilter, setMapFilter] = useState<MapFilter | null>(null);
  const [routes, setRoutes] = useState<Record<string, TrackingRoute>>({});
  // Real routes and positions, loaded after the arrivals so the page never
  // waits on the map. Without them the map estimates positions.
  const journeyIds = useMemo(
    () =>
      [
        ...new Set(
          arrivals.flatMap((arrival) =>
            arrival.journey ? [arrival.journey.id] : []
          )
        ),
      ]
        .sort()
        .join(','),
    [arrivals]
  );
  useEffect(() => {
    setRoutes({});
    if (!journeyIds) return;
    const controller = new AbortController();
    getTrackingRoutes(journeyIds.split(','), controller.signal)
      .then((value) => {
        if (!controller.signal.aborted) setRoutes(value);
      })
      .catch(() => undefined);
    return () => controller.abort();
    // request.data: reload routes after "Actualizar tracking" re-syncs them.
  }, [journeyIds, request.data]);
  const windowArrivals = useMemo(
    () => arrivalWindow(arrivals, horizon),
    [arrivals, horizon]
  );
  const arrivalMap = useMemo(
    () => buildArrivalMap(windowArrivals, routes, now),
    [windowArrivals, routes, now]
  );
  // A destination, origin or route picked on the map filters the KPIs and the
  // list. A pick that left the window no longer applies.
  const mapFilterSet = useMemo(
    () => (mapFilter ? mapFilterIds(arrivalMap, mapFilter) : null),
    [arrivalMap, mapFilter]
  );
  const activeFilter = mapFilterSet?.size ? mapFilter : null;
  const visibleArrivals = useMemo(
    () =>
      activeFilter && mapFilterSet
        ? windowArrivals.filter((arrival) => mapFilterSet.has(arrival.id))
        : windowArrivals,
    [windowArrivals, activeFilter, mapFilterSet]
  );
  const overdue = useMemo(() => overdueArrivals(arrivals), [arrivals]);
  const groups = useMemo(
    () => groupArrivals(visibleArrivals, groupBy),
    [groupBy, visibleArrivals]
  );
  const ageInfo = fruitAge(visibleArrivals);
  const [formatMappingOpen, setFormatMappingOpen] = useState(false);
  const openFormatMapping = useCallback(() => setFormatMappingOpen(true), []);
  const unmappedKg = unmappedFormatKg(visibleArrivals);
  const averageAge = ageInfo.days;
  const urgentCount = containerCount(
    visibleArrivals.filter((arrival) => arrival.urgent)
  );
  const delayedCount = containerCount(
    visibleArrivals.filter((arrival) => (arrival.delayDays ?? 0) > 0)
  );
  const kpis: Array<{
    label: string;
    value: string;
    tone?: 'danger' | 'warning' | 'success';
  }> = [
    {
      label: 'Embarques',
      value: String(containerCount(visibleArrivals)),
    },
    {
      label: 'Carga total',
      value: formatKg(
        visibleArrivals.reduce((sum, arrival) => sum + arrival.kg, 0)
      ),
    },
    {
      label: 'Clientes',
      value: String(
        new Set(visibleArrivals.map((arrival) => arrival.clientKey)).size
      ),
    },
    {
      label: 'Puertos destino',
      value: String(
        new Set(visibleArrivals.map((arrival) => arrival.pod)).size
      ),
    },
    {
      label: 'Arriban hoy y mañana',
      value: String(urgentCount),
      tone: urgentCount ? 'danger' : undefined,
    },
    {
      label: 'Con retraso vs. plan',
      value: String(delayedCount),
      tone: delayedCount ? 'warning' : undefined,
    },
    {
      label: 'Edad prom. fruta',
      value: averageAge === null ? 'Sin dato' : `${Math.round(averageAge)}d`,
      tone:
        averageAge === null
          ? undefined
          : averageAge > 45
            ? 'danger'
            : averageAge > 35
              ? 'warning'
              : 'success',
    },
  ];
  const timelineDays = Array.from({ length: 14 }, (_, offset) => {
    const dayArrivals = arrivalWindow(arrivals, 14).filter(
      (arrival) => arrival.offsetDays === offset
    );
    return {
      offset,
      date: dateForOffset(offset),
      value:
        timelineMetric === 'kg'
          ? dayArrivals.reduce((sum, arrival) => sum + arrival.kg, 0)
          : containerCount(dayArrivals),
    };
  });
  const maxTimelineValue = Math.max(...timelineDays.map((day) => day.value), 1);
  const toggleRecord = (
    setter: React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
    key: string
  ) => setter((current) => ({ ...current, [key]: !current[key] }));
  const refreshIds = [
    ...new Set(
      visibleArrivals
        .filter((arrival) => canRefreshArrival(arrival.journey))
        .map((arrival) => arrival.journey!.id)
    ),
  ];
  const handleRefresh = async (ids = refreshIds) => {
    await request.reload(async (signal) => {
      const refresh = await refreshArrivalTracking(ids, signal);
      signal.throwIfAborted();
      const result = await load(signal);
      return { ...result, refresh };
    });
  };
  const renderGroup = (group: ArrivalGroup) => (
    <GroupCard
      key={group.key}
      group={group}
      groupBy={groupBy}
      subGroupBy={subGroupBy}
      expandedRows={expandedRows}
      openCompositions={openCompositions}
      collapsedGroups={collapsedGroups}
      onToggleRow={(id) => toggleRecord(setExpandedRows, id)}
      managementSummaries={request.data?.management}
      onLogbook={(arrival) =>
        void details.handleViewDetails(
          arrival.reference,
          'logbook',
          arrival.journey ? [arrival.journey] : [],
          arrival.shipmentId
        )
      }
      onTracking={(arrival) =>
        void details.handleViewDetails(
          arrival.reference,
          'tracking',
          arrival.journey ? [arrival.journey] : [],
          arrival.shipmentId
        )
      }
      onToggleComposition={(key) => toggleRecord(setOpenCompositions, key)}
      onToggleCollapsed={(key) => toggleRecord(setCollapsedGroups, key)}
    />
  );

  return (
    <MapFormatsContext.Provider value={openFormatMapping}>
      <div className='h-screen overflow-y-auto bg-background'>
        <PageLayout>
          <PageHeader
            title='Gestor de Arribos'
            titleAddon={
              <Chip size='sm' variant='flat' color='warning'>
                Beta · datos reales
              </Chip>
            }
            description='Visualiza lo que está por arribar en los próximos días — por contenedor o AWB, cliente y puerto o aeropuerto de destino — para gestionar cada llegada a tiempo.'
            actions={
              <div className='flex flex-wrap items-center gap-3'>
                <span className='text-xs text-default-400'>
                  {request.data
                    ? `Datos leídos ${new Date(request.data.data.loadedAt).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}`
                    : 'Sin datos cargados'}
                </span>
                <Button
                  size='sm'
                  variant={showMap ? 'flat' : 'light'}
                  startContent={<Icon icon='tabler:map-2' width={17} />}
                  aria-pressed={showMap}
                  onPress={() => setShowMap((value) => !value)}
                >
                  {showMap ? 'Ocultar mapa' : 'Ver mapa'}
                </Button>
                <Button
                  size='sm'
                  variant='light'
                  isDisabled={!scope || request.loading}
                  onPress={() => void request.reload()}
                >
                  Recargar datos
                </Button>
                <Button
                  size='sm'
                  variant='flat'
                  isLoading={request.loading}
                  startContent={
                    !request.loading && (
                      <Icon icon='tabler:refresh' width={17} />
                    )
                  }
                  isDisabled={!scope || refreshIds.length === 0}
                  onPress={() => void handleRefresh()}
                >
                  Actualizar tracking
                </Button>
              </div>
            }
          />

          {!scope && (
            <p role='status' className='mt-4 text-sm text-default-500'>
              Selecciona una temporada y una especie para ver los arribos.
            </p>
          )}
          {request.loading && <LoadingState label='Cargando arribos…' />}
          {request.error && (
            <Alert variant='danger' showIcon className='mt-4'>
              {request.error}
              <Button
                size='sm'
                variant='light'
                onPress={() => void request.reload()}
              >
                Reintentar
              </Button>
            </Alert>
          )}
          {unmappedKg > 0 && (
            <Alert variant='warning' showIcon className='mt-4'>
              <div className='flex flex-wrap items-center justify-between gap-3'>
                <span>
                  {formatKg(unmappedKg)} de los arribos de la ventana tienen
                  formatos sin mapear (NO ENCONTRADO), así que no se ven en la
                  composición por formato.
                </span>
                <MapFormatsButton onPress={openFormatMapping} />
              </div>
            </Alert>
          )}
          {request.data?.managementError && (
            <Alert variant='warning' showIcon className='mt-4'>
              {t(
                'No se pudo cargar la actividad. Recarga los datos para reintentar.'
              )}
            </Alert>
          )}
          {request.data?.refresh && (
            <Alert
              variant={
                request.data.refresh.failures.length ? 'warning' : 'success'
              }
              showIcon
              className='mt-4'
            >
              Tracking: {request.data.refresh.succeeded} actualizado(s),{' '}
              {request.data.refresh.failures.length} con error.
              {request.data.refresh.failures.map((failure) => (
                <p key={failure.id} className='mt-1'>
                  {failure.message}
                </p>
              ))}
            </Alert>
          )}
          <Card shadow='sm' className='mt-6'>
            <CardBody className='grid gap-6 p-6 xl:grid-cols-[minmax(300px,1fr)_auto_auto] xl:items-center'>
              <div>
                <div className='flex items-baseline justify-between gap-3'>
                  <span className='text-xs font-semibold uppercase tracking-wide text-default-500'>
                    Ventana de arribos
                  </span>
                  <span className='font-bold text-primary'>
                    Próximos {horizon} {horizon === 1 ? 'día' : 'días'}
                  </span>
                </div>
                <input
                  type='range'
                  min={1}
                  max={14}
                  value={horizon}
                  className='mt-3 w-full cursor-pointer accent-primary'
                  aria-label='Ventana de arribos'
                  onChange={(event) => setHorizon(Number(event.target.value))}
                />
                <div className='mt-1 flex justify-between text-[11px] text-default-400'>
                  <span>1 día</span>
                  <span>14 días</span>
                </div>
              </div>
              <div>
                <span className='text-xs font-semibold uppercase tracking-wide text-default-500'>
                  Agrupar por
                </span>
                <div className='mt-2'>
                  <SegmentedControl
                    value={groupBy}
                    options={(
                      Object.keys(GROUP_LABELS) as ArrivalGroupKey[]
                    ).map((key) => ({ key, label: GROUP_LABELS[key] }))}
                    onChange={(key) => {
                      setGroupBy(key);
                      if (subGroupBy === key) setSubGroupBy('none');
                    }}
                  />
                </div>
              </div>
              <div>
                <span className='text-xs font-semibold uppercase tracking-wide text-default-500'>
                  Luego por
                </span>
                <div className='mt-2'>
                  <SegmentedControl
                    value={subGroupBy}
                    options={[
                      { key: 'none', label: '—' },
                      ...(Object.keys(GROUP_LABELS) as ArrivalGroupKey[]).map(
                        (key) => ({ key, label: GROUP_LABELS[key] })
                      ),
                    ]}
                    disabled={(key) => key === groupBy}
                    onChange={setSubGroupBy}
                  />
                </div>
              </div>
            </CardBody>
          </Card>

          {request.data && (
            <>
              <div className='mt-4 grid grid-cols-2 gap-3 md:grid-cols-4 2xl:grid-cols-7'>
                {kpis.map((kpi) => (
                  <KpiCard key={kpi.label} {...kpi} />
                ))}
              </div>

              {showMap && windowArrivals.length > 0 && (
                <ArrivalsMapPanel
                  map={arrivalMap}
                  arrivals={windowArrivals}
                  filtered={visibleArrivals}
                  horizon={horizon}
                  filter={activeFilter}
                  onFilter={setMapFilter}
                  onTracking={(arrival) =>
                    void details.handleViewDetails(
                      arrival.reference,
                      'tracking',
                      arrival.journey ? [arrival.journey] : [],
                      arrival.shipmentId
                    )
                  }
                />
              )}
              {activeFilter && (
                <div className='mt-3 flex flex-wrap items-center gap-2 text-sm'>
                  <span className='text-default-500'>
                    {activeFilter.kind === 'pod'
                      ? 'Filtrando por destino:'
                      : activeFilter.kind === 'origin'
                        ? 'Filtrando por origen:'
                        : 'Filtrando por ruta:'}
                  </span>
                  <Chip
                    size='sm'
                    variant='flat'
                    onClose={() => setMapFilter(null)}
                  >
                    {activeFilter.kind === 'pod'
                      ? `${visibleArrivals[0]?.flag ?? ''} ${activeFilter.label}`.trim()
                      : activeFilter.label}
                  </Chip>
                </div>
              )}

              {visibleArrivals.some((arrival) =>
                arrival.mix.some(
                  (line) => line.boxWeight <= 0 && line.quantity > 0
                )
              ) && (
                <p className='mt-3 text-xs text-default-500'>
                  Hay bultos sin peso válido; sus kilos no se incluyen en los
                  totales ni en la composición.
                </p>
              )}
              {request.data.data.cargo.some(
                (cargo) =>
                  normalizeTransport(cargo.ship_type) === 'M' &&
                  !cargo.container_number?.trim()
              ) && (
                <p className='mt-3 text-xs text-default-500'>
                  Hay despachos marítimos sin número de contenedor; no se pueden
                  asignar a un arribo.
                </p>
              )}
              {ageInfo.missingKg > 0 && (
                <p className='mt-3 text-xs text-default-500'>
                  {formatKg(ageInfo.missingKg)} sin fecha de embalaje válida. La
                  edad promedio usa solo los kilos con fecha.
                </p>
              )}
              <Card shadow='sm' className='mt-4'>
                <CardBody className='overflow-x-auto p-6'>
                  <div className='flex flex-wrap items-center justify-between gap-3'>
                    <span className='text-sm font-semibold'>
                      Arribos — próximos 14 días
                    </span>
                    <div className='flex items-center gap-3'>
                      <span className='hidden text-xs text-default-400 sm:block'>
                        Selecciona un día para ajustar la ventana
                      </span>
                      <SegmentedControl
                        value={timelineMetric}
                        options={[
                          { key: 'containers', label: 'Embarques' },
                          { key: 'kg', label: 'Kg' },
                        ]}
                        onChange={setTimelineMetric}
                      />
                    </div>
                  </div>
                  <div className='mt-4 flex min-w-[720px] gap-1 pb-2'>
                    {timelineDays.map((day) => {
                      const active = day.offset < horizon;
                      const height = day.value
                        ? Math.max(8, (day.value / maxTimelineValue) * 44)
                        : 3;
                      const valueLabel = day.value
                        ? timelineMetric === 'kg'
                          ? `${(day.value / 1000).toLocaleString('es-CL', { maximumFractionDigits: 1 })} t`
                          : day.value
                        : '';
                      return (
                        <button
                          key={day.offset}
                          type='button'
                          className='flex flex-1 cursor-pointer flex-col items-center gap-1 rounded-lg px-1 py-2 transition-colors hover:bg-default-100'
                          onClick={() => setHorizon(day.offset + 1)}
                        >
                          <span
                            className={cn(
                              'h-4 text-[11px] font-semibold',
                              active ? 'text-foreground' : 'text-default-300'
                            )}
                          >
                            {valueLabel}
                          </span>
                          <span className='flex h-11 w-full items-end justify-center'>
                            <span
                              className={cn(
                                'w-4 rounded-t',
                                active ? 'bg-primary' : 'bg-default-300'
                              )}
                              style={{ height }}
                            />
                          </span>
                          <span
                            className={cn(
                              'whitespace-nowrap text-[11px]',
                              day.offset === 0
                                ? 'font-bold text-primary'
                                : active
                                  ? 'text-default-500'
                                  : 'text-default-300'
                            )}
                          >
                            {WEEKDAYS[day.date.getDay()]} {day.date.getDate()}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </CardBody>
              </Card>

              <Card shadow='sm' className='mt-4'>
                <CardBody className='p-0'>
                  <button
                    type='button'
                    className='flex w-full items-center gap-3 bg-default-50 px-4 py-4 text-left transition-colors hover:bg-default-100 sm:px-5'
                    onClick={() => toggleRecord(setOpenCompositions, 'total')}
                  >
                    <Icon
                      icon='tabler:chart-bar'
                      width={19}
                      className='text-primary'
                    />
                    <span className='font-bold'>
                      Total — próximos {horizon}{' '}
                      {horizon === 1 ? 'día' : 'días'}
                    </span>
                    <Chip size='sm' variant='flat' color='danger'>
                      Composición total
                    </Chip>
                    <span className='ml-auto hidden text-xs text-default-500 sm:block'>
                      {summaryText(visibleArrivals)}
                    </span>
                    <Icon
                      icon='tabler:chevron-down'
                      width={18}
                      className={cn(
                        'transition-transform',
                        !openCompositions.total && '-rotate-90'
                      )}
                    />
                  </button>
                  {openCompositions.total && (
                    <CompositionPanel
                      arrivals={visibleArrivals}
                      title='Composición total'
                    />
                  )}
                </CardBody>
              </Card>

              {visibleArrivals.length === 0 && (
                <Card shadow='sm' className='mt-4'>
                  <CardBody className='p-6'>
                    <EmptyState
                      title='No hay arribos para esta ventana'
                      description='Prueba ampliarla hasta 14 días.'
                    />
                  </CardBody>
                </Card>
              )}
              {groups.map(renderGroup)}
              {overdue.length > 0 && (
                <div className='mt-6'>
                  <Button
                    variant='flat'
                    className='h-auto min-h-10 justify-start whitespace-normal py-2 text-left'
                    onPress={() => setShowOverdue((value) => !value)}
                  >
                    {unitLabel(overdue)} con ETA vencida sin arribo confirmado ·{' '}
                    {showOverdue ? 'Ocultar' : 'Ver'}
                  </Button>
                  {showOverdue &&
                    groupArrivals(overdue, groupBy).map(renderGroup)}
                </div>
              )}
              {arrivals.some((arrival) => arrival.eta === null) && (
                <p className='mt-4 text-sm text-default-500'>
                  {unitLabel(
                    arrivals.filter((arrival) => arrival.eta === null)
                  )}{' '}
                  sin ETA válida, fuera de la ventana.
                </p>
              )}

              <p className='py-6 text-center text-xs text-default-400'>
                {unitLabel(visibleArrivals)} en ventana de {horizon}{' '}
                {horizon === 1 ? 'día' : 'días'} · ETA de tracking o
                planificada, según disponibilidad
              </p>
            </>
          )}
          <ShipmentDetailsModal
            {...details.modalProps}
            onLogbookChanged={reloadArrivals}
            getReceiverLabel={getReceiverLabel}
            priceUser={
              canRegisterPrices && userProfile?.id
                ? {
                    id: userProfile.id,
                    name: [userProfile.first_name, userProfile.last_name]
                      .filter(Boolean)
                      .join(' '),
                  }
                : undefined
            }
          />
          <FormatMappingModal
            isOpen={formatMappingOpen}
            onClose={() => {
              setFormatMappingOpen(false);
              // Mappings update the boxes in the database; reload to show them.
              reloadArrivals();
            }}
            speciesId={selectedSpecies?.id}
            season={selectedSeason}
          />
        </PageLayout>
      </div>
    </MapFormatsContext.Provider>
  );
};

export default ArrivalManagerPage;
